/**
 * useImpactPathHandlers - Event handlers for ImpactPathView
 *
 * Extracted from ImpactPathView.jsx to keep files small and focused.
 * Handles node clicks, updates, and consequence creation.
 */

import { useCallback } from 'react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useNotice } from '../../components/DialogProvider';

/**
 * Build node hierarchy from root to current node
 * Returns array of nodes from root to current (inclusive)
 */
function buildNodeHierarchy(node, allPathways) {
  const hierarchy = [];
  let currentNode = node;

  // Build path from current node back to root
  while (currentNode) {
    hierarchy.unshift({
      id: currentNode.id,
      text: currentNode.text,
      description: currentNode.description || '',
      category: currentNode.category,
      depth: currentNode.depth || 0
    });

    // Find parent
    if (currentNode.parentId) {
      currentNode = allPathways.find(p => p.id === currentNode.parentId);
    } else {
      break;
    }
  }

  return hierarchy;
}

export function useImpactPathHandlers({
  // v2's AI-fact-checker switch. Undefined on v1 = the analysis runs, as it
  // always has (config/impactMapModes.js).
  factCheck,
  setSelectedNode,
  setModalVisible,
  setPathways,
  pathways,
  participantId,
  participantInfo,
  scenarioId,
  kid,
  instanceId,
  tulevaisuuskuva, // Current future - child nodes inherit this from parent context
  setAcademicReviewDraft = null // ({ parentNode, sources, rows }) => void — review before persisting AI triple
}) {
  const { t } = useTranslation();
  const notice = useNotice();

  /**
   * Handle node click - opens modal with node details
   */
  const handleNodeClick = useCallback((node) => {
    console.log('🖱️ Node clicked:', node);
    console.log('🔍 Node type:', node.type);
    console.log('🔍 Setting selectedNode and modalVisible=true');

    console.log('✅ Opening modal for node:', node.id);
    setSelectedNode(node);
    setModalVisible(true);
    console.log('✅ State updated - modal should be visible');
  }, [setSelectedNode, setModalVisible]);

  /**
   * Handle node update - saves title, description, position changes to backend
   */
  const handleNodeUpdate = useCallback(async (updatedNode) => {
    console.log('📝 Node update:', updatedNode);

    // Store previous state for rollback
    const previousNode = pathways.find(p => p.id === updatedNode.id);

    // Optimistic update
    setPathways(prev => prev.map(p =>
      p.id === updatedNode.id ? { ...p, ...updatedNode } : p
    ));

    // Save to backend
    try {
      const response = await fetch(
        API_ENDPOINTS.UPDATE_PATHWAY(updatedNode.id),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedNode)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Backend rejected update:', response.status, errorData);
        // Rollback optimistic update
        if (previousNode) {
          setPathways(prev => prev.map(p =>
            p.id === updatedNode.id ? previousNode : p
          ));
        }
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Node update saved:', data);
      return data;
    } catch (error) {
      console.error('❌ Failed to save node update:', error);
      throw error; // Re-throw so caller can handle
    }
  }, [setPathways, pathways]);

  /**
   * Handle save consequence - creates new child node
   */
  const handleSaveConsequence = useCallback(async (selectedNode, data) => {
    console.log('💾 Save consequence:', data);

    if (!selectedNode) return null;

    // Create a child node (consequence that happens AFTER the current node)
    // For virtual start nodes (client-side only), use null parentId to make it a root node
    const isVirtualStartNode = selectedNode.type === 'start' &&
      (selectedNode.id === 'start-node' || selectedNode.id.startsWith('scenario-') || selectedNode.id === 'instance-root');

    // Inherit tulevaisuuskuva from parent or context - critical for filtering
    const parentTulevaisuuskuva = selectedNode.metadata?.originalData?.tulevaisuuskuva ||
                                  selectedNode.tulevaisuuskuva ||
                                  tulevaisuuskuva;

    // Inherit scenario_id from parent when the URL doesn't have one (e.g. Kaikki/
    // instance view has no ?scenario= param). Without this, nodes added in Kaikki
    // view get scenario_id=NULL and the Omat view's `scenarioId=X` filter hides them.
    const parentScenarioId = selectedNode.metadata?.originalData?.scenarioId ||
                             selectedNode.metadata?.originalData?.scenario_id ||
                             selectedNode.scenarioId ||
                             selectedNode.scenario_id;

    // A node added directly under the scenario root is a muuttuja, not a
    // consequence: it belongs to the scenario rather than to whoever typed it.
    // Flagging it as a starter makes it render as "Muuttuja" and — per the
    // sharing rule in Submission.getPathways — visible in every participant's
    // view. Without the flag these stayed private to their author and were
    // labelled "Seuraus".
    const parentDepth = isVirtualStartNode
      ? -1
      : (selectedNode.metadata?.originalData?.depth ?? selectedNode.depth ?? 0);
    const parentType = selectedNode.metadata?.originalData?.type || selectedNode.type;
    const isVariableLevel = !isVirtualStartNode && parentDepth === 0 && parentType === 'scenario_root';

    const childNode = {
      id: crypto.randomUUID(), // Generate proper UUID for submissions table
      text: data.title,
      description: data.description || data.title,
      mood: 'neutral',
      category: 'technology',
      participantId: participantId,
      participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
      depth: isVirtualStartNode ? 0 : (selectedNode.metadata?.originalData?.depth || selectedNode.depth || 0) + 1,
      type: 'human',
      is_scenario_starter: isVariableLevel,
      parentId: isVirtualStartNode ? null : (selectedNode.metadata?.originalData?.id || selectedNode.id),
      // How this node follows from its parent (specs/impact-path-v2-prd.md §3).
      // v1 callers never set it, so it stays null = untyped.
      effectType: data.effectType || null,
      // Judged while writing (§4b); null = nobody has judged this node.
      valence: data.valence || null,
      valenceSource: data.valence ? 'human' : null,
      scenario_id: scenarioId || parentScenarioId || null,
      tulevaisuuskuva: parentTulevaisuuskuva, // Inherit future from parent context
      // Human/AI content separation (Dec 2025)
      human_text: data.human_text || null,
      ai_analysis: data.ai_analysis || null,
      ai_sources: data.ai_sources || [],
      references: data.ai_sources || [], // Mirror for frontend consistency
      node_analysis: data.node_analysis || null, // Pre-computed AI analysis scores
    };

    console.log('🔥 Creating child node:', childNode);

    // Add to pathways state (optimistic update)
    setPathways(prev => [...prev, childNode]);

    // Save to database
    try {
      const response = await fetch(
        API_ENDPOINTS.ADD_NODE(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instanceId: kid || instanceId,
            participantId: participantId,
            participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
            nodeData: childNode,
            factCheck
          })
        }
      );

      if (!response.ok) {
        console.error('❌ Failed to save consequence to database');
      } else {
        console.log('✅ Consequence saved to database');
        // AI node generation removed - user creates nodes manually

        // With the fact-checker on, ask what the new card connects to. Saving
        // no longer does this by itself (#1407). Fire-and-forget: proposals
        // arrive later as impact-path-suggestions-added.
        if (factCheck && !childNode.is_scenario_starter) {
          fetch(API_ENDPOINTS.IMPACT_PATH_NODE_SUGGEST(childNode.id), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ instanceId: kid || instanceId })
          }).catch((err) => console.error('❌ Node suggestion request failed:', err));
        }
      }
    } catch (error) {
      console.error('❌ Error saving consequence:', error);
      return null; // Return null on error
    }

    setModalVisible(false);
    setSelectedNode(null);

    // Return the new node ID for zooming
    return childNode.id;
  }, [participantId, participantInfo, scenarioId, kid, instanceId, tulevaisuuskuva, factCheck, setPathways, setModalVisible, setSelectedNode]);

  /**
   * Generate AI analysis nodes (blocker, possibility, question)
   * Creates 3 child nodes for the given parent node
   */
  const generateAINodes = useCallback(async (parentNode) => {
    console.log('🤖 Generating AI nodes for:', parentNode.text);

    // Create temporary loading node
    const loadingNodeId = crypto.randomUUID();
    const loadingNode = {
      id: loadingNodeId,
      text: t('impactPath.aiAnalysis.loadingTitle'),
      description: t('impactPath.aiAnalysis.loadingDescription'),
      mood: 'neutral',
      category: 'technology',
      participantId: participantId,
      participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
      depth: parentNode.depth + 1,
      type: 'ai-loading',
      parentId: parentNode.id,
      scenario_id: scenarioId || parentNode.scenarioId || parentNode.scenario_id || null
    };

    // Show loading node immediately
    setPathways(prev => [...prev, loadingNode]);

    try {
      // Step 1: Build hierarchy for context
      const hierarchy = buildNodeHierarchy(parentNode, pathways);
      const hierarchyText = hierarchy.map((h, i) =>
        `${i + 1}. ${h.text} (${h.category}, depth: ${h.depth})`
      ).join('\n');

      // Build parent nodes array (exclude current node)
      const parentNodesData = hierarchy
        .filter(h => h.id !== parentNode.id)
        .map(h => ({ id: h.id, text: h.text, description: h.description, category: h.category, depth: h.depth }));

      console.log('🔗 Built hierarchy:', hierarchyText);
      console.log('📍 Parent nodes:', parentNodesData.length);

      // Step 2: Get AI analysis with RAG-enhanced endpoint
      console.log('🎓 Using RAG-enhanced AI endpoint for analysis...');
      const question = t('impactPath.aiAnalysis.academicQuestion', { nodeText: parentNode.text });

      const analysisResponse = await fetch(
        API_ENDPOINTS.AI_ANALYZE_ACADEMIC(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question,
            hierarchyText,
            nodeText: parentNode.text,
            nodeCategory: parentNode.category || 'neutral',
            instanceId: kid || instanceId,
            scenarioId: scenarioId,
            parentNodes: parentNodesData
          })
        }
      );

      if (!analysisResponse.ok) {
        console.error('❌ AI analysis failed:', analysisResponse.status);

        // Parse error message
        let errorMessage = t('impactPath.aiAnalysis.serviceUnavailable');
        try {
          const errorData = await analysisResponse.json();
          if (errorData.error?.includes('overloaded')) {
            errorMessage = t('impactPath.aiAnalysis.serviceOverloaded');
          } else if (errorData.error?.includes('api_error')) {
            errorMessage = t('impactPath.aiAnalysis.serviceInternalError');
          }
        } catch (e) {
          // Use default error message
        }

        // Replace loading node with error node
        setPathways(prev => prev.map(node =>
          node.id === loadingNodeId
            ? {
                ...node,
                text: t('impactPath.aiAnalysis.analysisFailedTitle'),
                description: errorMessage,
                type: 'ai-error',
                mood: 'bad'
              }
            : node
        ));
        return;
      }

      const analysisData = await analysisResponse.json();
      // Written without research (#1421): a quiet note, the consequences still arrive
      if (analysisData.researchUnavailable) {
        notice.info(t(analysisData.researchUnavailable === 'no_datasets' ? 'impactPath.aiAnalysis.researchNoDatasets' : 'impactPath.aiAnalysis.researchSearchFailed'));
      }

      // Extract research sources from RAG response
      const sources = analysisData.sources || [];
      if (sources.length > 0) {
        console.log('📚 Research sources received:', sources.length);
        sources.forEach((source, i) => {
          console.log(`   [${i + 1}] ${source.citation} (${source.relevance}% relevant)`);
        });
      }

      let analysisText = analysisData.content[0].text.trim();

      // Clean markdown code blocks
      if (analysisText.startsWith('```json')) {
        analysisText = analysisText.replace(/^```json\s*/, '').replace(/\s*```$/, '');
      } else if (analysisText.startsWith('```')) {
        analysisText = analysisText.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      const analysis = JSON.parse(analysisText);
      console.log('🤖 AI Analysis:', analysis);

      // Step 2: Generate short titles for each AI node
      const generateTitle = async (fullText, nodeType) => {
        const titleResponse = await fetch(
          API_ENDPOINTS.AI_GENERATE_TITLE(),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fullText,
              nodeType,
              instanceId: kid || instanceId || null
            })
          }
        );

        if (titleResponse.ok) {
          const result = await titleResponse.json();
          return result.title || fullText.substring(0, 70);
        }
        return fullText.substring(0, 70);
      };

      const [blockerTitle, possibilityTitle, questionTitle] = await Promise.all([
        generateTitle(analysis.blocker, 'blocker'),
        generateTitle(analysis.possibility, 'possibility'),
        generateTitle(analysis.question, 'question')
      ]);

      const rows = [
        { emoji: '🚫', title: blockerTitle, description: analysis.blocker, mood: 'bad', category: 'bad' },
        { emoji: '✨', title: possibilityTitle, description: analysis.possibility, mood: 'good', category: 'good' },
        { emoji: '❓', title: questionTitle, description: analysis.question, mood: 'question', category: 'target' }
      ];

      console.log('🤖 AI triple ready for review:', rows.map((r) => r.title));

      setPathways((prev) => prev.filter((node) => node.id !== loadingNodeId));

      if (setAcademicReviewDraft) {
        setAcademicReviewDraft({ parentNode, sources, rows });
      } else {
        for (const row of rows) {
          const aiNode = {
            id: crypto.randomUUID(),
            text: `${row.emoji} ${row.title}`,
            description: row.description,
            mood: row.mood,
            category: row.category,
            participantId: participantId,
            participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
            depth: parentNode.depth + 1,
            type: 'ai-generated',
            parentId: parentNode.id,
            scenario_id: scenarioId || parentNode.scenarioId || parentNode.scenario_id || null,
            sources
          };
          const saveResponse = await fetch(
            API_ENDPOINTS.ADD_NODE(),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                instanceId: kid || instanceId,
                participantId: participantId,
                participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
                nodeData: aiNode
              })
            }
          );
          if (saveResponse.ok) {
            setPathways((prev) => [...prev, aiNode]);
          }
        }
      }

    } catch (error) {
      console.error('❌ AI generation error:', error);

      // Replace loading node with generic error
      setPathways(prev => prev.map(node =>
        node.id === loadingNodeId
          ? {
              ...node,
              text: t('impactPath.aiAnalysis.generationErrorTitle'),
              description: t('impactPath.aiAnalysis.generationErrorDescription'),
              type: 'ai-error',
              mood: 'bad'
            }
          : node
      ));
    }
  }, [participantId, participantInfo, scenarioId, kid, instanceId, pathways, setPathways, setModalVisible, setSelectedNode, setAcademicReviewDraft, notice, t]);

  const confirmAcademicReview = useCallback(async ({ parentNode, sources, rows }) => {
    for (const row of rows) {
      const aiNode = {
        id: crypto.randomUUID(),
        text: `${row.emoji} ${row.title.trim()}`,
        description: row.description.trim(),
        mood: row.mood,
        category: row.category,
        participantId: participantId,
        participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
        depth: parentNode.depth + 1,
        type: 'ai-generated',
        parentId: parentNode.id,
        scenario_id: scenarioId || parentNode.scenarioId || parentNode.scenario_id || null,
        sources: sources || []
      };
      const saveResponse = await fetch(API_ENDPOINTS.ADD_NODE(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instanceId: kid || instanceId,
          participantId: participantId,
          participantName: participantInfo?.name || `Participant ${participantId?.slice(0, 8)}`,
          nodeData: aiNode
        })
      });
      if (saveResponse.ok) {
        setPathways((prev) => [...prev, aiNode]);
      } else {
        console.error('❌ Failed to save AI node:', aiNode.text);
      }
    }
  }, [participantId, participantInfo, scenarioId, kid, instanceId, setPathways]);

  return {
    handleNodeClick,
    handleNodeUpdate,
    handleSaveConsequence,
    generateAINodes,
    confirmAcademicReview
  };
}
